import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Snippet } from '@snippix/shared';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { api } from '../api/client';

// Define the shape of our favourites context
interface FavouritesContextType {
  favourites: Snippet[];
  isLoading: boolean;
  error: string | null;
  fetchFavourites: () => Promise<void>;
  addFavourite: (snippetId: string) => Promise<boolean>;
  removeFavourite: (snippetId: string) => Promise<boolean>;
  isFavourite: (snippetId: string) => boolean;
  /**
   * Convenience helper that flips the favourite state for a snippet.
   * Returns the new state (true = now favourited, false = removed).
   */
  toggleFavourite: (snippetId: string) => Promise<boolean>;
}

// Create the context with a default value
const FavouritesContext = createContext<FavouritesContextType>({
  favourites: [],
  isLoading: false,
  error: null,
  fetchFavourites: async () => {},
  addFavourite: async () => false,
  removeFavourite: async () => false,
  isFavourite: () => false,
  toggleFavourite: async () => false,
});

// Custom hook to use the favourites context
export const useFavourites = () => useContext(FavouritesContext);

interface FavouritesProviderProps {
  children: ReactNode;
}

export const FavouritesProvider: React.FC<FavouritesProviderProps> = ({ children }) => {
  const [favourites, setFavourites] = useState<Snippet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();

  // Fetch favourites when the user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchFavourites();
    } else {
      // Clear favourites when not authenticated
      setFavourites([]);
    }
  }, [isAuthenticated, user?.id]);

  // Fetch all favourites for the current user
  const fetchFavourites = async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await api.get<Snippet[]>('/favourites');
      // Normaliseer zodat we altijd een array in de state zetten
      // (defensief tegen slechte API responses)
      const safeArray = Array.isArray(data) ? data : [];
      setFavourites(safeArray);
    } catch (error) {
      console.error('Error fetching favourites:', error);
      setError('Failed to fetch favourites');
      // Zorg dat de UI in een consistente toestand blijft
      setFavourites([]);
      showToast?.('Failed to fetch favourites', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Add a snippet to favourites
  const addFavourite = async (snippetId: string): Promise<boolean> => {
    if (!isAuthenticated) {
      showToast?.('You must be logged in to add favourites', 'warning');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      await api.post(`/favourites/${snippetId}`);

      // Refetch favourites to get the updated list
      await fetchFavourites();

      showToast?.('Snippet added to favourites', 'success');
      return true;
    } catch (error) {
      console.error('Error adding favourite:', error);
      setError('Failed to add to favourites');
      showToast?.('Failed to add to favourites', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove a snippet from favourites
  const removeFavourite = async (snippetId: string): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      setIsLoading(true);
      setError(null);

      await api.delete(`/favourites/${snippetId}`);

      // Update the local state by removing the snippet
      setFavourites(prev => prev.filter(fav => fav.id !== snippetId));

      showToast?.('Snippet removed from favourites', 'success');
      return true;
    } catch (error) {
      console.error('Error removing favourite:', error);
      setError('Failed to remove from favourites');
      showToast?.('Failed to remove from favourites', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a snippet is in favourites
  const isFavourite = (snippetId: string): boolean => {
    return favourites.some(fav => fav.id === snippetId);
  };

  // Toggle favourite status
  const toggleFavourite = async (snippetId: string): Promise<boolean> => {
    if (isFavourite(snippetId)) {
      return await removeFavourite(snippetId);
    } else {
      return await addFavourite(snippetId);
    }
  };

  return (
    <FavouritesContext.Provider
      value={{
        favourites,
        isLoading,
        error,
        fetchFavourites,
        addFavourite,
        removeFavourite,
        isFavourite,
        toggleFavourite,
      }}
    >
      {children}
    </FavouritesContext.Provider>
  );
};

export default FavouritesContext;
